<template>
  <div :class="props.height ? 'h-full flex flex-col' : 'flex flex-col'" :style="{ height: props.height || 'min(85vh, 1200px)' }">
    <CalendarToolbar
      :date-iso="localIso(focusedMonth)"
      :date-label-long="monthLabel"
      :hide-tab-switcher="true"
      :locked-teacher="lockedTeacher"
      :keep-long-date="!!lockedTeacher"
      :all-teachers="allTeachers"
      :all-instruments="allInstruments"
      :all-locations="allLocations"
      :active-teachers="activeTeachers"
      :selected-location="selectedLocation"
      :is-dark="isDark"
      :is-instrument-active="isInstrumentActive"
      :is-instrument-partial="isInstrumentPartial"
      :is-loading="isLoading"
      :readonly="readonly"
      @prev="prevMonth"
      @next="nextMonth"
      @jump-date="jumpToDate"
      @today="goToday"
      @refresh="fetchData"
      @new-lesson="openCreateModal(newLessonDefaultIso())"
      @toggle-teacher="toggleTeacher"
      @deselect-all="deselectAll"
      @toggle-instrument="toggleInstrument"
      @select-location="selectLocation"
    >
      <template #extra-controls>
        <slot name="extra-controls" />
      </template>
    </CalendarToolbar>

    <!-- Continuous month grid (Apple Calendar-style): one unbroken strip of week rows that
         scrolls vertically and lazy-extends at both ends, rather than paging a month at a
         time. The toolbar label tracks whichever month sits at the viewport's vertical
         middle; days outside that month are dimmed, and each month's 1st is labeled
         ("Oct 1") so the boundary reads while scrolling. Rows are a fixed pixel height
         (rowHeight) so prepending weeks can re-anchor scrollTop exactly. The weekday
         header stays outside the scroll container. -->
    <div class="rounded-lg border border-stroke dark:border-stroke-dark overflow-hidden flex-1 min-h-0 flex flex-col">
      <!-- Weekday header -->
      <div class="grid grid-cols-7 bg-surface-raised dark:bg-surface-raised-dark border-b border-stroke dark:border-stroke-dark flex-shrink-0">
        <div v-for="wd in WEEKDAY_LABELS" :key="wd"
             class="px-2 py-1.5 text-xs font-medium uppercase tracking-wide text-fg-muted dark:text-fg-muted-dark text-center">
          {{ wd }}
        </div>
      </div>

      <!-- Day cells -->
      <div class="relative flex-1 min-h-0 flex flex-col">
      <!-- First-load spinner as an overlay, so it never shifts the scroll geometry -->
      <div v-if="!loadedOnce" class="absolute inset-0 z-10 flex items-center justify-center bg-surface/60 dark:bg-surface-dark/60 pointer-events-none">
        <svg class="animate-spin h-6 w-6 text-fg-faint" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
      </div>
      <div ref="scrollEl" class="flex-1 min-h-0 overflow-y-auto overflow-x-hidden" @scroll.passive="onScroll">
      <div class="grid grid-cols-7" :style="{ gridAutoRows: rowHeight + 'px' }">
        <div
          v-for="day in gridDays" :key="day.iso"
          @click="activeDayIso = day.iso"
          class="relative border-r border-b border-stroke dark:border-stroke-dark p-1.5 cursor-pointer transition-colors hover:bg-surface-muted dark:hover:bg-surface-hover-dark overflow-hidden"
          :class="[
            day.monthKey === focusedMonthKey ? 'bg-surface dark:bg-surface-dark' : 'bg-surface-raised/40 dark:bg-surface-dark/20',
            day.isToday ? 'ring-1 ring-inset ring-blue-400 dark:ring-blue-500' : '',
          ]"
        >
          <div class="flex items-center justify-between mb-1">
            <span class="flex items-center gap-1">
              <span v-if="day.dayNum === 1" class="text-xs font-semibold"
                    :class="day.monthKey === focusedMonthKey ? 'text-fg-base dark:text-fg-base-dark' : 'text-fg-faint dark:text-fg-faint-dark'">{{ day.monthShort }}</span>
              <span :class="[
                'text-xs font-semibold w-5 h-5 flex items-center justify-center rounded-full',
                day.isToday
                  ? 'bg-blue-600 text-white'
                  : day.monthKey === focusedMonthKey
                    ? 'text-fg-base dark:text-fg-base-dark'
                    : 'text-fg-faint dark:text-fg-faint-dark'
              ]">{{ day.dayNum }}</span>
            </span>
            <button v-if="!readonly" @click.stop="openCreateModal(day.iso)"
              class="w-4 h-4 flex items-center justify-center rounded text-fg-faint dark:text-fg-faint-dark hover:bg-surface-hover dark:hover:bg-surface-hover-dark hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
              title="New lesson">
              <PlusIcon class="h-3 w-3" />
            </button>
          </div>

          <div class="space-y-0.5" :class="day.monthKey === focusedMonthKey ? '' : 'opacity-60'">
            <div v-for="name in day.timeOffTeachers" :key="'off-' + name"
                 class="flex items-center gap-1 text-[10px] leading-tight rounded px-1 py-0.5 truncate calendar-timeoff font-medium text-amber-800 dark:text-amber-300">
              <span>🚫</span>
              <span class="truncate">{{ lockedTeacher ? 'Unavailable' : `${name.split(' ')[0]} unavailable` }}</span>
            </div>
            <!-- Ghost chips (dashed): pending invites (blue — the family hasn't registered)
                 and lesson requests (amber — click to respond). -->
            <div v-for="l in day.visibleLessons" :key="l.id"
                 class="flex items-center gap-1 text-[10px] leading-tight rounded px-1 py-0.5 truncate"
                 :class="!l.ghost ? '' : l.request
                   ? 'border border-dashed border-amber-500 dark:border-amber-400 opacity-80 italic'
                   : 'border border-dashed border-blue-400 dark:border-blue-500 opacity-70 italic'"
                 :title="!l.ghost ? null : l.request ? ['Lesson request', l.instrument, l.age && `Age ${l.age}`].filter(Boolean).join(' · ') : 'Awaiting registration'"
                 :style="l.ghost ? {} : chipStyle(l.teacher_name)"
                 @click="l.request && ($event.stopPropagation(), openRequest = l.signup)">
              <span class="w-1.5 h-1.5 rounded-full shrink-0" :style="{ background: avatarGradient(l.teacher_name) }" />
              <span class="font-medium shrink-0">{{ fmtTime(l.lesson_time) }}</span>
              <!-- Locked (own-schedule) view: every chip is the same teacher, so name the student instead -->
              <span class="truncate">{{ lockedTeacher ? (l.student_name || l.contact_name || l.lesson_title || '—') : (l.teacher_name?.split(' ')[0] || '—') }}</span>
            </div>
            <div v-if="day.overflowCount > 0" class="text-[10px] text-fg-muted dark:text-fg-muted-dark px-1">
              +{{ day.overflowCount }} more
            </div>
          </div>
        </div>
      </div>
      </div>
      </div>
    </div>

    <DayAgendaModal
      v-if="activeDayIso"
      :date="activeDayIso"
      :lessons="lessonsForDay(activeDayIso)"
      :open-day-label="lockedTeacher ? 'Open Week View' : 'Open Day View'"
      @close="activeDayIso = null"
      @open-day="openDay(activeDayIso)"
      @lessons-removed="removeLessons"
    />

    <LessonCreateModal
      v-if="createProps"
      v-bind="createProps"
      @close="createProps = null"
      @saved="() => { createProps = null; fetchData() }"
    />

    <RequestCardModal v-if="openRequest" :signup="openRequest" @close="openRequest = null" @updated="openRequest = null; fetchData()" />
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { PlusIcon } from '@heroicons/vue/24/outline'
import { useApi } from '@/composables/useApi.js'
import { localIso, easternToday, getMonday, fmtTime, isLessonExcused, lessonsChange } from '@/composables/useCalendarData.js'
import { useCalendarFilters } from '@/composables/useCalendarFilters.js'
import { avatarGradient } from '@/composables/useAvatarGradient.js'
import CalendarToolbar from '@/components/calendar/CalendarToolbar.vue'
import DayAgendaModal from '@/components/calendar/DayAgendaModal.vue'
import RequestCardModal from '@/components/calendar/RequestCardModal.vue'
import { useRequestGhosts } from '@/composables/useRequestGhosts.js'
import LessonCreateModal from '@/components/common/LessonCreateModal.vue'
import { useDemoMode } from '@/composables/useDemoMode'
import { useAdminSidebar } from '@/composables/useAdminSidebar.js'

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const MIN_ROW_HEIGHT = 96
const ROWS_PER_VIEWPORT = 5   // row height = viewport / 5, so a typical month fills the view
const EXTEND_WEEKS = 8        // weeks added per lazy-load step at either end
const EDGE_ROWS = 3           // start extending when this close (in rows) to either end
const CHIP_PX = 18            // one lesson/time-off chip incl. gap — sizes how many fit per cell

const props = defineProps({
  hideTabSwitcher: { type: Boolean, default: false },  // unused here (no built-in switcher), kept for parity with Day/WeekView
  readonly:        { type: Boolean, default: false },  // suppress click-to-create-lesson (read-only embed)
  height:          { type: String, default: null },    // CSS length — forces the box to exactly this size regardless of content (for resizable panels)
  // Teacher's own-schedule embed (App.vue): single teacher, no filter bar, teacher-scoped
  // lesson endpoint, and "open day" hands back to the parent instead of routing to /admin.
  lessonEndpoint:  { type: String, default: '/api/admin/calendar' },
  showRequests:    { type: Boolean, default: false },  // teacher's own calendar: open lesson requests as ghosts (useRequestGhosts.js)
  lockedTeacher:   { type: String, default: null },
  hideExcused:     { type: Boolean, default: false },
})
const emit = defineEmits(['open-day'])

const currentUser = JSON.parse(localStorage.getItem('user') || 'null')
const effectivePermissions = useDemoMode().effectivePermissionsFor(() => currentUser?.permissions)
const hasSignupsPermission = computed(() => effectivePermissions.value.includes('admin') || effectivePermissions.value.includes('lesson_signups'))

// Admin pages open the form in the admin sidebar; the teacher dashboard's embed as a popup.
const adminSidebar = useAdminSidebar()
const createProps = ref(null)
const openCreateModal = iso => {
  const p = {
    initialDate:       iso,
    lockedTeacherName: hasSignupsPermission.value ? null : (props.lockedTeacher || currentUser?.name),
    lockedTeacherId:   hasSignupsPermission.value ? null : currentUser?.id,
    canManageSignups:  hasSignupsPermission.value,
  }
  if (adminSidebar.available.value) adminSidebar.open('lesson-create', p, { onSaved: fetchData })
  else createProps.value = p
}

const router = useRouter()
const { fetchApi } = useApi()

// ── Dark mode ─────────────────────────────────────────────────────────────────
const isDark = ref(document.documentElement.classList.contains('dark'))
let _darkObserver
onMounted(() => {
  _darkObserver = new MutationObserver(() => {
    isDark.value = document.documentElement.classList.contains('dark')
  })
  _darkObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })
})
onUnmounted(() => _darkObserver?.disconnect())

const chipStyle = name => {
  const neutral = isDark.value ? 'rgba(55,65,81,0.5)' : 'rgb(249,250,251)'
  return { background: `${avatarGradient(name, 'to bottom')} left/2px 100% no-repeat, ${neutral}` }
}

// ── Date helpers ──────────────────────────────────────────────────────────────
const addDays = (d, n) => { const r = new Date(d); r.setDate(r.getDate() + n); return r }
const monthStart = d => new Date(d.getFullYear(), d.getMonth(), 1)
const monthKeyOf = d => d.getFullYear() * 12 + d.getMonth()

const today = easternToday()
const todayIso = localIso(today)

// ── Loaded week range + scroll geometry ───────────────────────────────────────
// rangeStart is always a Monday; weekCount full weeks are rendered from it.
const rangeStart = ref(getMonday(addDays(monthStart(today), -7 * EXTEND_WEEKS)))
const weekCount  = ref(EXTEND_WEEKS * 3)
const rangeEnd   = computed(() => addDays(rangeStart.value, weekCount.value * 7 - 1))

const scrollEl  = ref(null)
const rowHeight = ref(110)
const scrollTop = ref(0)
const viewportH = ref(0)

const rowIndexOf = d => Math.round((getMonday(d) - rangeStart.value) / (7 * 86400000))

// The label/dimming follow the month of the Thursday in the row at the viewport's middle.
const focusedMonth = computed(() => {
  const row = Math.max(0, Math.min(weekCount.value - 1, Math.floor((scrollTop.value + viewportH.value / 2) / rowHeight.value)))
  return monthStart(addDays(rangeStart.value, row * 7 + 3))
})
const focusedMonthKey = computed(() => monthKeyOf(focusedMonth.value))
const monthLabel = computed(() =>
  focusedMonth.value.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
)

let _raf = 0
const onScroll = () => {
  if (_raf) return
  _raf = requestAnimationFrame(() => {
    _raf = 0
    const el = scrollEl.value
    if (!el) return
    scrollTop.value = el.scrollTop
    maybeExtend()
  })
}

// Near the top: prepend weeks and shift scrollTop by exactly the added height so the
// visible content doesn't move. Near the bottom: append (no re-anchoring needed).
const prependWeeks = n => {
  rangeStart.value = addDays(rangeStart.value, -7 * n)
  weekCount.value += n
  const el = scrollEl.value
  if (el) {
    nextTick(() => { el.scrollTop += n * rowHeight.value; scrollTop.value = el.scrollTop })
  }
  fetchRange(rangeStart.value, addDays(rangeStart.value, 7 * n - 1))
}
const appendWeeks = n => {
  const from = addDays(rangeEnd.value, 1)
  weekCount.value += n
  fetchRange(from, rangeEnd.value)
}
const maybeExtend = () => {
  const el = scrollEl.value
  if (!el || programmaticScroll) return
  const edge = EDGE_ROWS * rowHeight.value
  if (el.scrollTop < edge) prependWeeks(EXTEND_WEEKS)
  else if (el.scrollHeight - el.scrollTop - el.clientHeight < edge) appendWeeks(EXTEND_WEEKS)
}

// Scroll so the row holding `date`'s month-1st sits at the top. Makes sure there's a
// buffer of weeks on both sides first, so no lazy-extend fires (and re-anchors
// scrollTop) mid-way through a smooth scroll.
let programmaticScroll = false
let _programmaticTimer = 0
const scrollToMonth = async (date, smooth = true) => {
  const target = monthStart(date)
  const endOfTarget = new Date(target.getFullYear(), target.getMonth() + 1, 0)
  const before = rowIndexOf(target) - EXTEND_WEEKS
  if (before < 0) {
    const n = -before
    rangeStart.value = addDays(rangeStart.value, -7 * n)
    weekCount.value += n
    if (scrollEl.value) { await nextTick(); scrollEl.value.scrollTop += n * rowHeight.value }
    fetchRange(rangeStart.value, addDays(rangeStart.value, 7 * n - 1))
  }
  const after = rowIndexOf(endOfTarget) + EXTEND_WEEKS - (weekCount.value - 1)
  if (after > 0) appendWeeks(after)
  await nextTick()
  const el = scrollEl.value
  if (!el) return
  programmaticScroll = true
  clearTimeout(_programmaticTimer)
  _programmaticTimer = setTimeout(() => { programmaticScroll = false; onScroll() }, smooth ? 700 : 50)
  el.scrollTo({ top: rowIndexOf(target) * rowHeight.value, behavior: smooth ? 'smooth' : 'auto' })
  if (!smooth) scrollTop.value = el.scrollTop
}

const prevMonth = () => scrollToMonth(new Date(focusedMonth.value.getFullYear(), focusedMonth.value.getMonth() - 1, 1))
const nextMonth = () => scrollToMonth(new Date(focusedMonth.value.getFullYear(), focusedMonth.value.getMonth() + 1, 1))
const goToday   = () => scrollToMonth(today)
// Far jumps (date picker) reset the loaded window instead of scrolling through months of weeks.
const jumpToDate = iso => {
  const d = new Date(iso + 'T00:00:00')
  if (Math.abs(rowIndexOf(d) - rowIndexOf(focusedMonth.value)) <= 26) return scrollToMonth(d)
  rangeStart.value = getMonday(addDays(monthStart(d), -7 * EXTEND_WEEKS))
  weekCount.value = EXTEND_WEEKS * 3
  lessons.value = []
  cancellations.value = []
  fetchData()
  scrollToMonth(d, false)
}

// Row height: a fifth of the viewport (min MIN_ROW_HEIGHT). On resize, keep the same
// fractional row at the top.
let _resizeObs
onMounted(() => {
  const el = scrollEl.value
  const measure = () => {
    const topRow = el.scrollTop / rowHeight.value
    viewportH.value = el.clientHeight
    rowHeight.value = Math.max(MIN_ROW_HEIGHT, Math.floor(el.clientHeight / ROWS_PER_VIEWPORT))
    el.scrollTop = topRow * rowHeight.value
    scrollTop.value = el.scrollTop
  }
  _resizeObs = new ResizeObserver(measure)
  _resizeObs.observe(el)
  measure()
  scrollToMonth(today, false)
})
onUnmounted(() => { _resizeObs?.disconnect(); cancelAnimationFrame(_raf); clearTimeout(_programmaticTimer) })

// Toolbar "+ New Lesson": today when it's in the focused month, else that month's 1st.
const newLessonDefaultIso = () =>
  monthKeyOf(today) === focusedMonthKey.value ? todayIso : localIso(focusedMonth.value)

const activeDayIso = ref(null)
const openDay = iso => props.lockedTeacher
  ? (activeDayIso.value = null, emit('open-day', iso))
  : router.push({ path: '/admin/calendar/day', query: { date: iso } })

// ── Data ──────────────────────────────────────────────────────────────────────
const lessons       = ref([])
const teachers      = ref([])
const cancellations = ref([])  // marked time-off windows: [{ teacher_name, start_datetime, end_datetime, reason }]
const ghosts        = ref([])  // pending invites (`${lessonEndpoint}-ghosts`), tagged ghost: true
const { requestGhosts, fetchRequests } = useRequestGhosts()
const openRequest   = ref(null)  // signup whose card is open
const pending       = ref(0)
const isLoading     = computed(() => pending.value > 0)
const loadedOnce    = ref(false)

const teacherMap = computed(() => new Map(teachers.value.map(t => [t.name, t])))
const allTeachers = computed(() => {
  const names = new Set([
    ...teachers.value.map(t => t.name),
    ...lessons.value.map(l => l.teacher_name).filter(Boolean),
  ])
  return [...names].sort()
})
const allInstruments = computed(() => {
  const insts = new Set()
  teachers.value.forEach(t => (t.instruments || []).forEach(i => insts.add(i)))
  return [...insts].sort()
})
const allLocations = computed(() => {
  const locs = new Set()
  lessons.value.forEach(l => { if (l.location) locs.add(l.location) })
  const sorted = [...locs].sort()
  if (lessons.value.some(l => !l.location)) sorted.unshift('__none__')
  return sorted
})

const {
  activeTeachers, selectedLocation,
  toggleTeacher, deselectAll,
  toggleInstrument, isInstrumentActive, isInstrumentPartial,
  selectLocation, matchesLocation,
  filteredTeachers,
} = useCalendarFilters({ allTeachers, teacherMap, shared: !props.lockedTeacher })

if (!props.lockedTeacher) {
  fetchApi('/api/teacher-availability/roster').then(t => { teachers.value = t }).catch(() => {})
}

// Fetch one date segment and merge it in, replacing whatever was loaded for those dates.
const fetchRange = async (from, to) => {
  const start = localIso(from), end = localIso(to)
  pending.value++
  try {
    const [lessonData, cancellationData, ghostData, requestData] = await Promise.all([
      fetchApi(`${props.lessonEndpoint}?start_date=${start}&end_date=${end}`),
      fetchApi(`/api/teacher-cancellations/range?start_date=${start}T00:00:00&end_date=${end}T23:59:59`).catch(() => []),
      fetchApi(`${props.lessonEndpoint}-ghosts?start_date=${start}&end_date=${end}`).catch(() => []),
      // Admin calendar: every teacher's open requests, date-scoped like invites
      props.showRequests ? [] : fetchApi(`${props.lessonEndpoint}-request-ghosts?start_date=${start}&end_date=${end}`).catch(() => []),
    ])
    const outside = l => l.lesson_date < start || l.lesson_date > end
    lessons.value = [...lessons.value.filter(outside), ...lessonData]
    ghosts.value = [...ghosts.value.filter(outside), ...[...ghostData, ...requestData].map(g => ({ ...g, ghost: true }))]
    const seen = new Set(cancellations.value.map(c => `${c.teacher_name}::${c.start_datetime}`))
    cancellations.value = [...cancellations.value, ...cancellationData.filter(c => !seen.has(`${c.teacher_name}::${c.start_datetime}`))]
  } catch {
    console.error('Failed to fetch calendar data')
  } finally {
    pending.value--
    loadedOnce.value = true
  }
}
// Full refresh of everything currently loaded (toolbar refresh, lesson saved elsewhere).
const fetchData = () => Promise.all([
  fetchRange(rangeStart.value, rangeEnd.value),
  props.showRequests && fetchRequests(props.lockedTeacher),
])
watch(lessonsChange, () => fetchData())
fetchData()

// Lessons grouped by date once per change, instead of re-filtering the whole list per cell.
const lessonsByDate = computed(() => {
  const map = new Map()
  for (const l of lessons.value) {
    if (props.lockedTeacher
      ? (props.hideExcused && isLessonExcused(l))
      : !(filteredTeachers.value.includes(l.teacher_name) && matchesLocation(l))) continue
    if (!map.has(l.lesson_date)) map.set(l.lesson_date, [])
    map.get(l.lesson_date).push(l)
  }
  for (const list of map.values()) list.sort((a, b) => (a.lesson_time || '').localeCompare(b.lesson_time || ''))
  return map
})
const lessonsForDay = iso => lessonsByDate.value.get(iso) || []

const ghostsForDay = iso => [...ghosts.value, ...requestGhosts.value.map(g => ({ ...g, ghost: true }))].filter(g => g.lesson_date === iso && (props.lockedTeacher
  ? g.teacher_name === props.lockedTeacher
  : filteredTeachers.value.includes(g.teacher_name) && matchesLocation(g)))

// Distinct teacher names marked off (any portion of) this day, for the month-cell chip.
const timeOffTeachersForDay = iso => {
  const dayStart = new Date(`${iso}T00:00:00`)
  const dayEnd   = new Date(`${iso}T23:59:59`)
  const names = new Set()
  for (const c of cancellations.value) {
    if (props.lockedTeacher ? c.teacher_name !== props.lockedTeacher : !filteredTeachers.value.includes(c.teacher_name)) continue
    if (new Date(c.end_datetime) <= dayStart || new Date(c.start_datetime) >= dayEnd) continue
    names.add(c.teacher_name)
  }
  return [...names]
}

// Drops locally-known lessons (e.g. ones soft-deleted by an "end series" edit)
// without a refetch — see cancelled_lesson_ids returned from /lesson-edits.
const removeLessons = (ids) => {
  if (!ids?.length) return
  const idSet = new Set(ids)
  lessons.value = lessons.value.filter(l => !idSet.has(l.id))
}

// ── Grid days ─────────────────────────────────────────────────────────────────
// Chips that fit under the date row; one slot is kept for "+N more" when overflowing.
const chipSlots = computed(() => Math.max(1, Math.floor((rowHeight.value - 34) / CHIP_PX)))
const gridDays = computed(() => {
  const days = []
  const slots = chipSlots.value
  for (let i = 0; i < weekCount.value * 7; i++) {
    const d = addDays(rangeStart.value, i)
    const iso = localIso(d)
    const dayLessons = [...lessonsForDay(iso), ...ghostsForDay(iso)]
      .sort((a, b) => (a.lesson_time || '').localeCompare(b.lesson_time || ''))
    const timeOff = timeOffTeachersForDay(iso)
    const room = Math.max(0, slots - timeOff.length)
    const fitsAll = dayLessons.length <= room
    const shown = fitsAll ? dayLessons.length : Math.max(0, room - 1)
    days.push({
      iso,
      dayNum: d.getDate(),
      monthKey: monthKeyOf(d),
      monthShort: d.toLocaleDateString('en-US', { month: 'short' }),
      isToday: iso === todayIso,
      visibleLessons: dayLessons.slice(0, shown),
      overflowCount: dayLessons.length - shown,
      timeOffTeachers: timeOff,
    })
  }
  return days
})
</script>
